import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getCompanyNotificationSettings,
  bulkUpdatePreferences,
} from '../../../../api/General/NotificationPreference';
import type {
  CompanyNotificationSettingView,
  NotificationPreferenceItemInput,
} from '../../../../api/General/NotificationPreference';
import NotificationPreferenceMatrix from '../../../../components/notifications/NotificationPreferenceMatrix';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '../../../../components/ui/Dialog';
import Button from '../../../../components/ui/Button';
import { Spinner } from '../../../../components/ui/Spinner';
import AppIcon from '../../../../components/AppIcon';

interface BulkPreferencesModalProps {
  open: boolean;
  onClose: () => void;
  selectedCount: number;
  filter: {
    departmentId?: number;
    branchId?: number;
    role?: string;
    search?: string;
  };
  onApplied: () => void;
}

const BulkPreferencesModal: React.FC<BulkPreferencesModalProps> = ({
  open,
  onClose,
  selectedCount,
  filter,
  onApplied,
}) => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<CompanyNotificationSettingView[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load company settings as the baseline for bulk editing
  useEffect(() => {
    if (!open) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await getCompanyNotificationSettings();
        if (result.errors?.length) {
          setError(
            result.errors[0]?.description || t('Failed to load settings')
          );
          return;
        }
        setSettings(result.payload ?? []);
      } catch (err) {
        console.error('Failed to load company settings for bulk:', err);
        setError(t('Failed to load settings'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open, t]);

  const handleSettingChange = useCallback(
    (notificationTypeId: number, field: string, value: boolean) => {
      setSettings((prev) =>
        prev.map((s) => {
          if (s.notificationTypeId !== notificationTypeId) return s;
          return { ...s, [field]: value };
        })
      );
    },
    []
  );

  const handleApply = async () => {
    setSaving(true);
    setError(null);
    try {
      // Convert the admin-style settings to preference items
      // For bulk update: enabled = !disabled
      const preferences: NotificationPreferenceItemInput[] = settings.map(
        (s) => ({
          notificationTypeId: s.notificationTypeId,
          emailEnabled: !s.emailsDisabled,
          pushEnabled: !s.mobilePushNotificationsDisabled,
          inAppEnabled: !s.inAppDisabled,
        })
      );

      const result = await bulkUpdatePreferences({
        filter,
        preferences,
      });

      if (result.errors?.length) {
        setError(
          result.errors[0]?.description || t('Failed to apply settings')
        );
        return;
      }

      onApplied();
    } catch (err) {
      console.error('Failed to bulk update preferences:', err);
      setError(t('Failed to apply settings'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(val) => !val && onClose()}>
      <DialogContent size="xl">
        <DialogHeader>
          <DialogTitle>{t('Bulk Update Preferences')}</DialogTitle>
          <DialogDescription>
            {t(
              'These settings will be applied to {{count}} selected user(s). This will override their current preferences.',
              { count: selectedCount }
            )}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {error && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive flex items-center gap-2 mb-4">
              <AppIcon name="AlertCircle" size={16} className="shrink-0" />
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-8">
              <Spinner size="lg" />
            </div>
          ) : (
            <NotificationPreferenceMatrix
              mode="admin"
              companySettings={settings}
              onCompanySettingChange={handleSettingChange}
            />
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('Cancel')}
          </Button>
          <Button
            onClick={handleApply}
            disabled={saving || loading}
            loading={saving}
          >
            {t('Apply Settings')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkPreferencesModal;
